import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CarIcon, ButtonLargeTextStyle, Flex } from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { translateRoute } from '~/App/shared/localization';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';
import {
  FloatingButtonVisibility,
  useFloatingButtonVisibility
} from '~/App/shared/hooks/useFloatingButtonVisibility';
import { useRegistrationPlateSubmit } from '~/App/shared/hooks/useRegistrationPlateSubmit';
import { useRecaptcha } from '~/App/shared/components/Auth/hooks/authHooks';
import ValuationPopup from './ValuationPopup';
import { useLocation } from 'react-router-dom';

type ContainerProps = {
  $bottom: FloatingButtonVisibility['bottom'];
  $isHidden: boolean;
};

const Container = styled.div<ContainerProps>`
  position: fixed;
  left: 0.75rem;
  bottom: ${({ $bottom }) => $bottom};
  z-index: 1001;

  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;

  width: auto;
  min-width: 3.75rem;
  height: 3.75rem;
  padding: 0 1.25rem;

  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.dark};
  background-color: ${({ theme }) => theme.colors.primary.main};
  box-shadow: ${({ theme }) => theme.elevations.elevation16};
  border-radius: 3.75rem;

  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
  opacity: ${({ $isHidden }) => ($isHidden ? '0' : '1')};
  transition: opacity 100ms ease-in-out;
`;

const PillContent = styled(Flex)`
  user-select: none;
`;

const Text = styled.span`
  ${ButtonLargeTextStyle}
`;

const ValuationButton = () => {
  const { t } = useTranslation();
  const { isGreaterThan, isBeingDetermined } = useIsDevice();
  const { isHidden, bottom } = useFloatingButtonVisibility();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const submit = useRegistrationPlateSubmit();
  const { pathname } = useLocation();

  useRecaptcha();

  const encodedLocation = encodeURIComponent(pathname);
  const isBlockedRoute = [
    'ROUTES.SELL',
    'ROUTES.SELL_HEAVY_EQUIPMENT',
    'ROUTES.PAYMENT'
  ].some(route => encodedLocation.includes(translateRoute(t, route)));

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      const container = containerRef.current;

      if (isOpen && container && !container.contains(event?.target as Node)) {
        setIsOpen(false);
      }
    },
    [isOpen]
  );

  useEffect(() => {
    window.addEventListener('click', handleOutsideClick);

    return () => window.removeEventListener('click', handleOutsideClick);
  }, [handleOutsideClick]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [setIsOpen]);

  const toggleOpen = () => setIsOpen(open => !open);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOpen();
    }
  };

  if (isBeingDetermined || isBlockedRoute) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      $bottom={bottom}
      $isHidden={isHidden}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onClick={toggleOpen}
      onKeyDown={handleKeyDown}
    >
      <PillContent gap="0.625rem">
        {isGreaterThan('tablet') && (
          <span>
            <CarIcon fontSize="1.5rem" />
          </span>
        )}
        <Text>{t('Free valuation')}</Text>
      </PillContent>

      {isOpen && (
        <ValuationPopup
          {...submit}
          onContainerClick={e => e.stopPropagation()}
        />
      )}
    </Container>
  );
};

export default ValuationButton;
